import { AppAuth } from './AppAuth.js';
import { AppDB } from './AppDB.js';
import { StackManager } from './navigation/StackManager.js';
import { ControllerLoader } from './navigation/ControllerLoader.js';
import { PreloadManager } from './navigation/PreloadManager.js';

import localforage from 'localforage';
import mustache from 'mustache';

import { SERVICE_WORKER_CACHE_VERSION } from '../AppVersion.js';

import gtag from 'ga-gtag';
import { pageTouch } from '../utils/Ui';

/**
 * Gestor principal de la aplicación WETAG
 * Implementa patrón Singleton para mantener una única instancia
 * @class AppManager
 */
class AppManager {
    /**
     * Constructor del AppManager (Singleton)
     */
    constructor() {
        if (AppManager._instance) {
            return AppManager._instance;
        }

        /** @type {AppAuth|null} */
        this.auth = null;
        /** @type {AppDB|null} */
        this.db = null;
        /** @type {Object|null} */
        this.settings = null;

        /** @type {boolean} */
        this.initialized = false;
        /** @type {HTMLElement|null} */
        this.oldPage = null;
        /** @type {boolean} */
        this.isLoading = false;

        /** @type {StackManager} */
        this.stackManager = new StackManager();
        /** @type {ControllerLoader} */
        this.controllerLoader = new ControllerLoader();
        /** @type {PreloadManager} */
        this.preloadManager = new PreloadManager(this.controllerLoader);

        this.assignTargetEvents(document.body);

        AppManager._instance = this;
    }

    /**
     * Inicializa la aplicación
     * @async
     * @returns {Promise<void>}
     * @throws {Error} Si hay error en la inicialización
     */
    async init() {
        if (this.initialized) {
            return;
        }

        try {
            this.auth = new AppAuth();
            this.db = new AppDB();
            this.settings = {
                restUrl: 'https://crm.wetag.ar/app',
                contentUrl: 'https://crm.wetag.ar/content'
            };

            window.App = this;
            window.AppAuth = this.auth;
            window.AppDB = this.db;
            window.AppSettings = this.settings;

            await this.auth.init(this);
            await this.db.init(this);

            this.initialized = true;
            this.oldPage = null;

            const isLoggedIn = await this.auth.isAuthenticated();
            if (!isLoggedIn) {
                const startParams = new URLSearchParams(window.location.search);
                if (startParams.get('page') === 'experience') {
                    await this.load('experience');
                } else {
                    await this.load('login');
                }
            } else {
                await this.loadInitialPage();
            }

            setTimeout(() => this.preloadManager.preloadPages(), 5000);
        } catch (error) {
            this.handleError('App initialization error', error);
            throw error;
        }
    }

    /**
     * Carga la página inicial según parámetros URL o última página restaurable
     * @async
     * @returns {Promise<boolean>} true si se cargó correctamente
     */
    async loadInitialPage() {
        try {
            const params = new URLSearchParams(window.location.search);
            const page = params.get('page');
            const validPages = ['trip', 'profile', 'explore', 'experience'];

            if (page && validPages.includes(page)) {
                await this.load(page);
            } else {
                const lastPage = await this.stackManager.getLastRestorablePage();
                if (lastPage) {
                    await this.load(lastPage.controllerName, lastPage.args);
                } else {
                    await this.load('trip');
                }
            }
            return true;
        } catch (error) {
            this.handleError('Initial page load error', error);
            return false;
        }
    }

    /**
     * Resetea la aplicación
     * @async
     * @returns {Promise<void>}
     */
    async reset() {
        this.auth = null;
        this.db = null;
        this.settings = null;

        this.initialized = false;
        this.oldPage = null;
        this.isLoading = false;

        delete window.App;
        delete window.AppAuth;
        delete window.AppDB;
        delete window.AppSettings;

        await this.init();
    }

    /**
     * Carga un controlador y renderiza la página
     * @async
     * @param {string} controllerName - Nombre del controlador
     * @param {Object|boolean} args - Argumentos para el controlador
     * @param {number} scrollPosition - Posición de scroll a restaurar
     * @returns {Promise<boolean>} true si se cargó correctamente
     */
    async load(controllerName, args = false, scrollPosition = 0) {
        if (this.isLoading) {
            console.warn(`[AppManager] Already loading, ignoring request for: ${controllerName}`);
            return false;
        }

        try {
            const isLoggedIn = await this.auth.isAuthenticated();
            const publicControllers = ['login', 'experience'];
            if (!isLoggedIn && !publicControllers.includes(controllerName)) {
                if (this.getCurrentControllerName() !== 'login') {
                    await this.load('login');
                }
                return false;
            }

            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.size > 0) {
                args = args || {};

                if (urlParams.has('reload')) {
                    args._reload = true;
                    urlParams.delete('reload');
                }
                args.params = Object.fromEntries(urlParams);
                const newUrl = window.location.pathname + window.location.hash;
                window.history.replaceState({}, '', newUrl);
            }

            this.isLoading = true;
            pageTouch(true, true);

            const oldPage = document.querySelector('.app-page');
            if (oldPage) {
                this.oldPage = await this.stackManager.saveToStack(oldPage, (page) => this.getSavedStatusElements(page));
            }

            const module = await this.getController(controllerName);
            if (!module || typeof module.default !== 'function') {
                await this.loadInitialPage();
                this.isLoading = false;
                pageTouch(false);
                return false;
            }

            const page = document.createElement('div');
            page.className = 'app-page z-1 position-relative';
            page.setAttribute('data-controller', controllerName);
            page.style.opacity = 0;
            document.body.appendChild(page);

            const restorable = await this.controllerLoader.isRestorable(controllerName);
            if (restorable) {
                page.setAttribute('data-restorable', true);
                if (args) {
                    args.restorable = true;
                } else {
                    args = { restorable: true };
                }
            }

            await this.stackManager.addToStack(controllerName, args, scrollPosition);

            Object.keys(module)
                .filter(key => key !== 'default' && typeof module[key] === 'function')
                .forEach(fn => {
                    window[fn] = module[fn];
                });

            try {
                await module.default(page, args, scrollPosition);
            } catch (error) {
                this.isLoading = false;
                pageTouch(false);
                this.handleError(`Controller Error: ${controllerName}`, error);
                return false;
            }

            this.isLoading = false;

            return true;
        } catch (error) {
            this.isLoading = false;
            pageTouch(false);
            this.handleError(`Load controller error: ${controllerName}`, error);
            return false;
        }
    }

    /**
     * Renderiza una página usando un template Mustache
     * @async
     * @param {HTMLElement} page - Elemento de la página
     * @param {string} templateFile - Ruta del template
     * @param {number} scrollPosition - Posición de scroll
     * @param {Object} fields - Datos para el template
     * @returns {Promise<HTMLElement>} Elemento de la página renderizado
     */
    async render(page, templateFile, scrollPosition = 0, fields = {}) {
        return new Promise(async (resolve, reject) => {
            const controllerName = this.getCurrentControllerName(page);
            if (!controllerName) {
                reject(new Error('No Controller Found'));
                return;
            }

            this.preloadManager.getTemplate(templateFile)
                .then((template) => {
                    this.buildTemplate(template, page, fields, scrollPosition, controllerName, resolve);
                })
                .catch(error => {
                    this.handleError(`Error loading template ${templateFile}`, error);
                    reject(error);
                });
        });
    }

    /**
     * Construye el template renderizado y configura eventos
     * @param {string} template - Template de Mustache
     * @param {HTMLElement} page - Elemento de la página
     * @param {Object} fields - Datos para el template
     * @param {number} scrollPosition - Posición de scroll
     * @param {string} controllerName - Nombre del controlador
     * @param {Function} resolve - Función de resolución de la promesa
     */
    buildTemplate(template, page, fields, scrollPosition, controllerName, resolve) {
        page.innerHTML = mustache.render(template, fields);

        this.assignTargetEvents(page);
        this.assingBackEvents(page);
        this.assignSaveStatusElementsEvents(controllerName, page);

        gtag('event', 'screen_view', { screen_name: controllerName });

        pageTouch(false);

        const event = new CustomEvent('App:Rendered', { detail: { page, controllerName, fields } });
        document.dispatchEvent(event);

        requestAnimationFrame(async () => {
            try {
                if (!page || !document.body.contains(page)) {
                    console.warn(`[AppManager] Page element no longer exists: ${controllerName}`);
                    resolve(page);
                    return;
                }

                if (this.oldPage && this.oldPage !== page) {
                    this.oldPage.remove();
                    this.oldPage = null;
                }

                document.querySelectorAll('.app-page').forEach(otherPage => {
                    if (otherPage !== page) otherPage.remove();
                });

                const content = page.querySelector('.app-content');
                if (content) content.scrollTop = scrollPosition;

                page.style.transition = 'opacity 0.3s ease';
                if (page && page.style) {
                    page.style.opacity = 1;
                }

                const mainNav = document.querySelector('.app-main-nav');
                if (mainNav) {
                    const hideNav = await this.controllerLoader.hasHideMainNav(controllerName);
                    if (hideNav) {
                        mainNav.classList.add('d-none');
                    } else {
                        mainNav.classList.remove('d-none');
                    }
                }
            } catch (error) {
                this.handleError('Error in buildTemplate', error);
                if (page && page.style && document.body.contains(page)) {
                    page.style.opacity = 1;
                }
                pageTouch(false);
            }
            resolve(page);
        });
    }

    /**
     * Asigna eventos a elementos con data-target
     * @async
     * @param {HTMLElement} target - Elemento contenedor
     * @returns {Promise<void>}
     */
    async assignTargetEvents(target) {
        if (!target) return;

        target.querySelectorAll('[data-target]').forEach(el => {
            el.addEventListener('click', (e) => {
                e.preventDefault();
                el.blur();

                const target = el.getAttribute('data-target');
                const args = el.getAttribute('data-args') || false;
                if (target) {
                    try {
                        this.load(target, JSON.parse(args));
                    } catch (e) {
                        this.load(target, args);
                    }
                }
            });
        });
    }

    /**
     * Asigna eventos a elementos con data-back
     * @async
     * @param {HTMLElement} target - Elemento contenedor
     * @returns {Promise<void>}
     */
    async assingBackEvents(target) {
        if (!target) return;
        const controllerName = this.getCurrentControllerName(target);
        target.querySelectorAll('[data-back]').forEach(el => {
            el.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
                this.loadPreviousPage(controllerName);
            });
        });
    }

    /**
     * Asigna observadores para elementos con data-save-status
     * @async
     * @param {string} controllerName - Nombre del controlador
     * @param {HTMLElement} page - Elemento de la página
     * @returns {Promise<void>}
     */
    async assignSaveStatusElementsEvents(controllerName, page) {
        const saveStatusElements = page.querySelectorAll('[data-save-status]');
        saveStatusElements.forEach(element => {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'data-status') {
                        this.setSavedStatusElements(controllerName, this.getSavedStatusElements(page));
                    }
                });
            });

            observer.observe(element, {
                attributes: true,
                attributeFilter: ['data-status']
            });
        });
    }

    /**
     * Carga la página anterior del stack
     * @async
     * @param {string|false} currentControllerName - Nombre del controlador actual
     * @returns {Promise<null>}
     */
    async loadPreviousPage(currentControllerName = false) {
        const stack = await this.stackManager.getStack();
        if (stack.length < 1) return null;

        const last = stack[stack.length - 1];
        if (currentControllerName && last.controllerName === currentControllerName) {
            if (stack.length < 2) return null;
            await this.stackManager.removeFromStack();
        }

        const updatedStack = await this.stackManager.getStack();
        const previous = updatedStack[updatedStack.length - 1];
        if (previous) {
            await this.load(previous.controllerName, previous.args, previous.scrollPosition || 0);
        }
        return null;
    }

    /**
     * Agrega una página al stack
     * @async
     * @param {string} controllerName - Nombre del controlador
     * @param {Object|boolean} args - Argumentos
     * @param {number} scrollPosition - Posición de scroll
     * @returns {Promise<void>}
     */
    async addToStack(controllerName, args = false, scrollPosition = 0) {
        return this.stackManager.addToStack(controllerName, args, scrollPosition);
    }

    /**
     * Remueve una página del stack
     * @async
     * @returns {Promise<void>}
     */
    async removeFromStack() {
        return this.stackManager.removeFromStack();
    }

    /**
     * Establece los elementos con estado guardado
     * @async
     * @param {string} currentControllerName - Nombre del controlador actual
     * @param {Object} elementsStatus - Estados de los elementos
     * @returns {Promise<void>}
     */
    async setSavedStatusElements(currentControllerName, elementsStatus) {
        return this.stackManager.setSavedStatusElements(currentControllerName, elementsStatus);
    }

    /**
    * Obtiene los elementos con estado guardado
    * @param {HTMLElement} page - Elemento de la página
    * @returns {Object} Objeto con los estados guardados
    */
    getSavedStatusElements(page) {
        const elements = page.querySelectorAll('[data-save-status][data-status="open"]');
        const status = {};
        elements.forEach(el => {
            const key = el.getAttribute('data-save-status');
            const value = el.getAttribute('data-status');
            if (key) status[key] = value;
        });

        return status;
    }

    /**
     * Obtiene la última página restaurable
     * @async
     * @returns {Promise<Object|null>}
     */
    async getLastRestorablePage() {
        return this.stackManager.getLastRestorablePage();
    }

    /**
     * Limpia el stack
     * @async
     * @returns {Promise<void>}
     */
    async clearStack() {
        return this.stackManager.clearStack();
    }

    /**
     * Fuerza el preload de un controlador y template
     * @async
     * @param {string} controllerName - Nombre del controlador
     * @param {string|null} templateFile - Ruta del template (opcional)
     * @returns {Promise<void>}
     */
    async forcePreload(controllerName, templateFile) {
        try {
            await Promise.all([
                this.controllerLoader.preloadController(controllerName),
                templateFile ? this.preloadManager.preloadTemplate(templateFile) : Promise.resolve()
            ]);
            console.info(`[AppManager] Forced Preload Done: ${controllerName}`);
        } catch (error) {
            this.handleError(`Error Forced Preloading - ${controllerName}`, error);
        }
    }

    /**
     * Limpia el cache de preload
     */
    clearPreload() {
        this.controllerLoader.controllerCache.clear();
        this.preloadManager.templateCache.clear();
        console.info('[AppManager] Preload Cache Cleared');
    }

    /**
     * Obtiene un controlador por nombre (con cache)
     * @async
     * @param {string} controllerName - Nombre del controlador
     * @returns {Promise<Object|null>} Módulo del controlador o null
     */
    async getController(controllerName) {
        return this.controllerLoader.getController(controllerName);
    }

    /**
     * Obtiene el nombre del controlador actual
     * @param {HTMLElement|null} page - Elemento de la página (opcional)
     * @returns {string|null} Nombre del controlador o null
     */
    getCurrentControllerName(page = null) {
        if (page) {
            return page.getAttribute('data-controller') || null;
        }
        const currentPage = document.querySelector('.app-page');
        return currentPage ? currentPage.getAttribute('data-controller') || null : null;
    }


    /**
     * Obtiene la versión del service worker (misma cadena que `version` en service-worker.js).
     * Usa `navigator.serviceWorker.ready` para no leer antes de que exista un worker activo
     * (p. ej. tras desregistrar y recargar, o si register() aún no terminó).
     * @async
     * @returns {Promise<string|null>} Versión o null
     */
    async getVersion() {
        try {
            if (!('serviceWorker' in navigator)) {
                return SERVICE_WORKER_CACHE_VERSION;
            }

            let registration;
            try {
                registration = await navigator.serviceWorker.ready;
            } catch {
                return SERVICE_WORKER_CACHE_VERSION;
            }

            if (!registration?.active) {
                return SERVICE_WORKER_CACHE_VERSION;
            }

            const versionFromSw = await new Promise((resolve) => {
                const ms = 4000;
                const to = setTimeout(() => resolve(null), ms);
                const messageChannel = new MessageChannel();
                messageChannel.port1.onmessage = (event) => {
                    clearTimeout(to);
                    resolve(event.data?.version ?? null);
                };
                try {
                    registration.active.postMessage({ type: 'GET_VERSION' }, [messageChannel.port2]);
                } catch {
                    clearTimeout(to);
                    resolve(null);
                }
            });

            return versionFromSw || SERVICE_WORKER_CACHE_VERSION;
        } catch (error) {
            this.handleError('Get Service Worker Version Error', error);
            return SERVICE_WORKER_CACHE_VERSION;
        }
    }

    /**
     * Guarda archivos en el cache del service worker
     * @async
     * @param {string[]} files - URLs de archivos a cachear
     * @returns {Promise<void>}
     */
    async saveToCache(files = []) {
        if (!Array.isArray(files) || files.length === 0) {
            return;
        }
        if (!('caches' in window)) {
            return;
        }

        try {
            const version = await this.getVersion();
            if (!version) {
                return;
            }

            const cache = await caches.open(version);
            await Promise.allSettled(
                files.map(file => cache.add(file))
            );
        } catch (error) {
            this.handleError('Cache Save Error', error);
        }
    }

    /**
     * Maneja errores de forma centralizada
     * @param {string} message - Mensaje del error
     * @param {Error|*} error - Objeto de error
     */
    handleError(message, error) {
        console.error(`[AppManager] ${message}`, error);

        const errorMessage = error instanceof Error ? error.message : (error?.message || String(error));
        const isAuthError = errorMessage && (
            errorMessage.includes('401') ||
            errorMessage.includes('Unauthorized') ||
            errorMessage.includes('Authentication token not available') ||
            errorMessage.includes('Token Incorrecto') ||
            errorMessage.includes('Encabezado Auth Inexistente') ||
            errorMessage.includes('Token Inexistente')
        );

        if (isAuthError) {
            console.warn('[AppManager] Authentication error detected, logging out');
            if (this.auth) {
                this.auth.logout().catch(logoutError => {
                    console.error('[AppManager] Error logging out', logoutError);
                });
            }
        }
    }
}

export default AppManager;
